import React from "react";
import ProfileContainer from "./ProfileContainer";
import useBoolean from "../hooks/useBoolean";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import * as yup from "yup";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { AmlStatus, OnboardingModel, StatusNaming } from "../constant/amlConstant";
import { LoadingButton } from "@mui/lab";
import { StatusUpdatePayload } from "../api/transformers/aml";
import { AmlAPI } from "../api";
import useConfirmDialog from "../hooks/useConfirmDialog";
import useMessage from "../hooks/useMessage";
import useStatusEditValidation from "../hooks/useStatusEditValidation";
import { usePermissionAction } from "../hooks/usePermissionAction";

type RecordOutcomeProfileProps = {
  personId: string;
};

type FormikValue = {
  comment: string;
  agreed: boolean;
  outcome: string;
};

const outcomeOptions = [
  {
    value: AmlStatus.ACT,
    label: StatusNaming[AmlStatus.ACT],
  },
  {
    value: AmlStatus.REJ,
    label: StatusNaming[AmlStatus.REJ],
  },
];

const validationSchema = yup.object({
  outcome: yup.string().required("This Field is Required."),
  agreed: yup.boolean().when("outcome", {
    is: (v: string) => v === AmlStatus.ACT,
    then: (schema) => schema.oneOf([true], "Please Confirm."),
    otherwise: (schema) => schema.notRequired(),
  }),
  comment: yup.string().when("outcome", {
    is: (v: string) => v === AmlStatus.REJ,
    then: (schema) => schema.required("This Field is Required."),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const RecordOutcomeProfile: React.FC<RecordOutcomeProfileProps> = ({
  personId,
}) => {
  const checkAmlUpdatePermission = usePermissionAction("update:investor-aml");
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] =
    useBoolean();
  const { confirm: showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { validate } = useStatusEditValidation();
  const message = useMessage();
  const {
    data: amlDetail,
    loading,
    fetchData: refetchAmlDetail,
  } = useInvestorAmlDetailStore();
  const availableOutcomeOptions = React.useMemo(() => {
    if (amlDetail) {
      return outcomeOptions.filter((el) => el.value !== amlDetail.status);
    }
    return outcomeOptions;
  }, [amlDetail]);
  const formik = useFormik<FormikValue>({
    initialValues: {
      agreed: false,
      comment: "",
      outcome: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = buildPayload(values);
      if (!check(payload)) {
        return;
      }
      handleSubmit(payload);
    },
  });

  const handleSubmit = (payload: StatusUpdatePayload) => {
    if (amlDetail?.onboardingModel === OnboardingModel.NONE_RELIANCE && (payload.status === AmlStatus.ACT || payload.status === AmlStatus.REJ)) {
      showConfirmDialog({
        title: "Confirm Notification",
        content: "Send a notification to advise the outcome?",
        cancelText: "No",
        okText: "Yes",
        onOk: () => {
          startSubmitting();
          payload.sendNotification = true;
          return updateStatus(payload);
        },
        onCancel() {
          startSubmitting();
          payload.sendNotification = false;
          return updateStatus(payload);
        },
      });
    } else {
      startSubmitting();
      updateStatus(payload);
    }
  };

  const updateStatus = (payload: StatusUpdatePayload) => {
    return AmlAPI.updateStatus(personId, payload)
      .then(() => {
        formik.resetForm();
        refetchAmlDetail(personId);
        message.success("Successfully Submitted.");
      })
      .catch((error) => {
        console.error(error);
        message.error("Error! We couldn't send the details for approval.");
      })
      .finally(() => {
        finishSubmitting();
      });
  };

  const check = (paylaod: StatusUpdatePayload) => {
    if (paylaod.status === AmlStatus.ACT) {
      return validate();
    }
    return true;
  };

  return (
    <ProfileContainer
      id="record-outcome"
      title="Record Outcome"
      loading={submitting}
    >
      <Stack direction="column" rowGap="6px">
        <FormControl fullWidth>
          <TextField
            name="comment"
            label="Reviwer Comments"
            multiline
            disabled={submitting}
            rows={4}
            placeholder="You can write a note here"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.comment && Boolean(formik.errors.comment)}
            helperText={formik.touched.comment && formik.errors.comment}
            inputProps={{ maxLength: 200 }}
          />
          <Box display="flex" justifyContent="flex-end">
            <FormHelperText>{`${
              formik.values.comment.length
            }/${200}`}</FormHelperText>
          </Box>
        </FormControl>
        <Box sx={{ marginLeft: "-9px" }}>
          <Checkbox
            size="small"
            name="agreed"
            checked={formik.values.agreed}
            color="primary"
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            disabled={submitting}
          />
          <label htmlFor="agreed">
            I can confirm that documentation has been uploaded to support my
            decision for AML verification.
          </label>
          {formik.touched.agreed && formik.errors.agreed && (
            <FormHelperText sx={{ marginLeft: "9px" }} error>
              {formik.errors.agreed}
            </FormHelperText>
          )}
        </Box>
        <TextField
          select
          name="outcome"
          label="Outcome"
          size="small"
          disabled={submitting}
          placeholder="Choose Outcome"
          sx={{
            width: "300px",
          }}
          value={formik.values.outcome}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={formik.touched.outcome && Boolean(formik.errors.outcome)}
          helperText={formik.touched.outcome && formik.errors.outcome}
          InputProps={{
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
          SelectProps={{
            MenuProps: {
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  p: 0,
                },
              },
            },
          }}
        >
          {availableOutcomeOptions.map((el) => (
            <MenuItem key={el.value} value={el.value}>
              {el.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction="row" justifyContent="flex-end">
        <LoadingButton
          variant="contained"
          color="primary"
          sx={{ borderRadius: "4px" }}
          onClick={checkAmlUpdatePermission(formik.submitForm)}
          loading={submitting}
        >
          Submit
        </LoadingButton>
      </Stack>
      {ConfirmDialog}
    </ProfileContainer>
  );
};

function buildPayload(values: FormikValue): StatusUpdatePayload {
  return {
    status: values.outcome,
    comment: values.comment,
    sendNotification: false,
  };
}

export default RecordOutcomeProfile;
